// src/components/forms/mapping-editor/CheckboxMappingsEditor.tsx (Create this file)
import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { CheckBoxMappingValue } from '../../../../interfaces/dynamicForms/PdfMapping';
import FormInput from '../formBuilder/FormInput';

interface CheckboxMappingsEditorProps {
  mappings: Record<string, CheckBoxMappingValue> | undefined;
  updateMappings: (newMappings: Record<string, CheckBoxMappingValue>) => void;
}

const CheckboxMappingsEditor: React.FC<CheckboxMappingsEditorProps> = ({ mappings = {}, updateMappings }) => {
  // State for the "Add New" form inputs
  const [newFormFieldId, setNewFormFieldId] = useState('');
  const [newYesField, setNewYesField] = useState('');
  const [newNoField, setNewNoField] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  // Handler for changes within an existing mapping's row
  const handleMappingChange = useCallback((
    formFieldId: string, // The key of the mapping
    fieldName: keyof CheckBoxMappingValue, // 'Yes' or 'No'
    pdfFieldValue: string
  ) => {
    const currentMapping = mappings[formFieldId];
    if (currentMapping) {
      const updatedMappingValue = {
        ...currentMapping,
        [fieldName]: pdfFieldValue || undefined, // Store empty as undefined? Or empty string? Keep string.
      };
      // Create a new top-level mappings object for immutability
      const newMappings = {
        ...mappings,
        [formFieldId]: updatedMappingValue,
      };
      updateMappings(newMappings);
    }
  }, [mappings, updateMappings]);

  // Handler to remove an existing mapping
  const handleRemoveMapping = useCallback((formFieldIdToRemove: string) => {
    // Create a new object excluding the key to remove
    const newMappings = { ...mappings };
    delete newMappings[formFieldIdToRemove];
    updateMappings(newMappings);
  }, [mappings, updateMappings]);

  // Handler to add a new mapping from the "Add New" inputs
  const handleAddMapping = useCallback(() => {
    setAddError(null); // Clear previous errors
    const key = newFormFieldId.trim();
    if (!key) {
      setAddError('Form Field ID cannot be empty.');
      return;
    }
    if (mappings[key] !== undefined) {
      setAddError(`Form Field ID "${key}" already exists.`);
      return;
    }
    if (!newYesField.trim() && !newNoField.trim()) {
      setAddError('At least one PDF field ("Yes" or "No") must be provided.');
      return;
    }

    const newMappingValue: CheckBoxMappingValue = {
      Yes: newYesField.trim() || undefined, // Store empty as undefined or ''? Keep as string for consistency
      No: newNoField.trim() || undefined,
    };

    const newMappings = {
      ...mappings,
      [key]: newMappingValue,
    };
    updateMappings(newMappings);

    // Reset add form
    setNewFormFieldId('');
    setNewYesField('');
    setNewNoField('');
  }, [mappings, updateMappings, newFormFieldId, newYesField, newNoField]);

  const mappingEntries = Object.entries(mappings);

  return (
    <div className="p-4 border rounded bg-white shadow-sm mb-6">
      <h3 className="text-lg font-semibold mb-1 text-gray-700">Checkbox Mappings (Yes/No)</h3>
      <p className="text-xs text-gray-500 mb-4">
        Map form fields (like radio buttons with 'Yes'/'No' values) to specific PDF checkbox fields.
      </p>

      {/* List of existing mappings */}
      <div className="space-y-3 mb-4">
        {mappingEntries.length > 0 ? (
          mappingEntries.map(([formFieldId, value]) => (
            <div key={formFieldId} className="border rounded p-3 bg-gray-50 relative grid grid-cols-1 md:grid-cols-7 gap-3 items-center">
              <div className="md:col-span-2">
                <FormInput
                  label="Form Field ID"
                  id={`cbmap-form-${formFieldId}`}
                  value={formFieldId}
                  readOnly // Don't allow editing keys directly, remove and re-add instead
                  className="bg-gray-100 text-gray-600"
                  title="To change Form Field ID, remove and re-add the mapping."
                />
              </div>
               <div className="md:col-span-2">
                <FormInput
                  label="PDF Field for 'Yes'"
                  id={`cbmap-yes-${formFieldId}`}
                  value={value.Yes || ''}
                  onChange={(e) => handleMappingChange(formFieldId, 'Yes', e.target.value)}
                  placeholder="e.g., CheckBox_Yes_1"
                />
               </div>
               <div className="md:col-span-2">
                <FormInput
                  label="PDF Field for 'No'"
                  id={`cbmap-no-${formFieldId}`}
                  value={value.No || ''}
                   onChange={(e) => handleMappingChange(formFieldId, 'No', e.target.value)}
                   placeholder="e.g., CheckBox_No_1"
                />
               </div>
               <div className="md:col-span-1 flex items-end justify-end pt-5">
                 <button
                    type="button"
                    onClick={() => handleRemoveMapping(formFieldId)}
                    className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-100 focus:outline-none focus:ring-1 focus:ring-red-400"
                    title={`Remove mapping for ${formFieldId}`}
                    aria-label={`Remove mapping for ${formFieldId}`}
                  >
                    <FontAwesomeIcon icon={faTrashAlt} /> Remove
                  </button>
               </div>
            </div>
          ))
        ) : (
          <p className="text-sm text-gray-500 italic">No Yes/No checkbox mappings added yet.</p>
        )}
      </div>

      {/* Form to Add New Mapping */}
      <div className="border-t pt-4">
        <h4 className="text-md font-medium mb-2 text-gray-600">Add New Checkbox Mapping</h4>
         <div className="grid grid-cols-1 md:grid-cols-7 gap-3 items-end">
             <div className="md:col-span-2">
                 <FormInput
                     label="Form Field ID"
                     id="cbmap-new-form"
                     value={newFormFieldId}
                     onChange={(e) => setNewFormFieldId(e.target.value)}
                     placeholder="e.g., q_problemServices"
                     required
                 />
             </div>
             <div className="md:col-span-2">
                 <FormInput
                     label="PDF Field for 'Yes'"
                     id="cbmap-new-yes"
                     value={newYesField}
                     onChange={(e) => setNewYesField(e.target.value)}
                      placeholder="e.g., Check Box12"
                 />
             </div>
              <div className="md:col-span-2">
                 <FormInput
                     label="PDF Field for 'No'"
                     id="cbmap-new-no"
                     value={newNoField}
                     onChange={(e) => setNewNoField(e.target.value)}
                      placeholder="e.g., Check Box8"
                 />
             </div>
             <div className="md:col-span-1 pb-1"> {/* Align button */}
                <button
                    type="button"
                    onClick={handleAddMapping}
                    className="w-full inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm font-medium rounded shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                    <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
                    <span className="ml-2">Add</span>
                </button>
             </div>
         </div>
         {addError && <p className="text-xs text-red-600 mt-1">{addError}</p>}
      </div>
    </div>
  );
};

export default CheckboxMappingsEditor;